// Retry timeout, rate-limiting, internal server error, bad gateway, service unavailable, gateway timeout, insufficient storage, and bandwidth limit exceeded http codes.
const RETRYABLE_HTTP_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504, 507, 509]);

type WithExponentialBackoffResult<T> = {response: Response; error: null} | {response: null; error: T};

/**
 * Calls the endpoint defined by fetchFn, backing off exponentially on retryable http status codes and network errors.
 *
 * @param fetchFn - the fetch function to access whatever endpoint we want to do exponential backoff on
 * @param options - configuration parameters for the exponential backoff
 * @param options.maxRetries - the maximum number of retries before we return an unreachable error. Defaults to 3.
 * @param options.delayFactorInSeconds - the factor to multiply the exponential waiting times by. Defaults to 1, so
 *   (ignoring the random jitter we add), the waiting times in seconds would be 1, 2, 4, 8, and so on.
 *   If we set the delayFactor to 3, the waiting times in seconds would be 3, 6, 12, 24, and so on.
 * @param options.errorWhenRetriesExhausted - the error we will return (not throw) if retries are exhausted.
 */
export async function withExponentialBackoff<T>(
  fetchFn: () => Promise<Response>,
  options: {
    maxRetries?: number;
    delayFactorInSeconds?: number;
    errorWhenRetriesExhausted: T;
  }
): Promise<WithExponentialBackoffResult<T>> {
  const {maxRetries = 3, delayFactorInSeconds = 1, errorWhenRetriesExhausted} = options;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Delay in milliseconds. Note: ** has higher precedence than *, so 2 ** attempt is evaluated first.
    // We add random jitter so multiple requests don't fire in lockstep while backing off.
    const delayForRetry = 1000 * delayFactorInSeconds * 2 ** attempt + Math.random() * 1000;
    try {
      const response = await fetchFn();
      if (!response.ok && RETRYABLE_HTTP_STATUS_CODES.has(response.status)) {
        // Retry on retryable http error codes (usually indicating some transient error)
        await new Promise((resolve) => {
          setTimeout(resolve, delayForRetry);
        });
      } else {
        // If response is ok or the error code is not retryable (something wrong with our input), just return the response
        return {response, error: null};
      }
    } catch (error) {
      if (error instanceof TypeError || error.name === 'AbortError') {
        // Retry on network errors and request cancellations
        await new Promise((resolve) => {
          setTimeout(resolve, delayForRetry);
        });
      } else {
        // If it's a different type of error, throw
        throw error;
      }
    }
  }
  // Hit max retries
  return {
    response: null,
    error: errorWhenRetriesExhausted,
  };
}
